import React from 'react';
import {
  AccordionItem,
  AccordionTrigger,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Separator,
} from '@ais-ui/ui-core';
import { cn } from '@ais-ui/utils';
import {
  PiCircle,
  PiDotsThreeVertical,
  PiLighthouse,
  PiQuestion,
} from 'react-icons/pi';
import { AtonHost } from '@ais-ui/api';
import { useLocalization } from '@ais-ui/locale-management';
import { ShowAtonButton } from './ShowAtonButton';
import {
  DetailView,
  useViewMode,
  ViewMode,
} from '../../../components/ViewModeProvider';
import { noop } from 'lodash';
import { useSelectionState } from '../../../components/SelectionStateProvider';
import { HostAtonItemContent } from './HostAtonItemContent';

interface HostAtonItemProps {
  aton: AtonHost;
  requiresMap: boolean;
}

export const HostAtonItem = ({ aton, requiresMap }: HostAtonItemProps) => {
  const { messages } = useLocalization();

  const { setViewMode } = useViewMode();

  const { setAton } = useSelectionState();

  // Determine icon color based on service states
  const getIconColor = (aton: AtonHost): string => {
    if (!aton.serviceStates) {
      return 'text-gray-500'; // Default color if no service states
    }

    // Red if any critical services
    if (aton.serviceStates.critical > 0) {
      return 'text-red-500';
    }

    // Yellow if any warning services but no critical
    if (aton.serviceStates.warning > 0) {
      return 'text-yellow-500';
    }

    // Green if all services are OK (and there are services)
    if (aton.serviceStates.ok > 0) {
      return 'text-green-500';
    }

    // Gray for unknown/pending states
    return 'text-gray-500';
  };

  const getIconForSiteType = (
    siteType: string | undefined,
    iconColor: string
  ) => {
    const baseIcon = (() => {
      if (!siteType) {
        return PiQuestion;
      }

      switch (siteType.toLowerCase()) {
        case 'lighthouse':
          return PiLighthouse;
        case 'buoy':
          return PiCircle;
        case 'aton':
          return PiCircle;
        default:
          return PiQuestion;
      }
    })();

    const IconComponent = baseIcon;
    return <IconComponent className={cn('h-4 w-4', iconColor)} />;
  };

  const onGoToDetails = () => {
    setAton(aton);
    setViewMode(ViewMode.Detail, DetailView.Aton);
  };

  const iconColor = getIconColor(aton);

  return (
    <AccordionItem
      key={aton.id}
      value={aton.id as string}
      className={cn('pr-4 pl-2')}
    >
      <div className="flex justify-between items-center">
        <div className="flex items-center">
          <div className="flex space-x-1 items-center">
            <ShowAtonButton
              onApply={noop}
              isApplied={false}
              disabled={true}
            ></ShowAtonButton>
            <Separator orientation="vertical" className="h-6 bg-gray-300" />
          </div>

          <AccordionTrigger className="w-[205px]" hideChevron={true}>
            <div className="flex ml-4 space-x-2 items-center">
              {getIconForSiteType(aton.siteType, iconColor)}
              <div className="ml-4 space-y-1">
                <p className="text-sm text-left font-medium leading-none group-hover:underline">
                  {aton.title}
                </p>
              </div>
            </div>
          </AccordionTrigger>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              id={`${aton.title?.toLowerCase()}-dropdown-button`}
              size="icon"
              variant="ghost"
              className="h-8 w-8"
            >
              <PiDotsThreeVertical className="h-6 w-6" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" onClick={onGoToDetails}>
            <DropdownMenuItem>{messages.atonListDetailAction}</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <HostAtonItemContent aton={aton} />
    </AccordionItem>
  );
};
